/**
 * Sweeping the commitment backlog, a topic at a time.
 *
 * WHY THIS EXISTS. Measured on his live vault, 2026-08-05: 1,064 open commitments, and 927 of
 * the 1,266 items in his attention feed are commitments. Closing worked one row at a time, from
 * a panel that showed four rows and capped at twelve. At one click per row the backlog is
 * roughly a thousand clicks, which means it never gets cleared, which means the numbers on his
 * dashboard drift away from reality in one direction until he stops trusting them.
 *
 * THE ORGANISING IDEA is the topic, not the date. Commitments arrive in clusters because they
 * are extracted from threads: his busiest topic carries 22 of them, and 183 topics carry two or
 * more. A thread that concluded leaves behind a whole group that is done together, so the unit
 * of judgement is the group. He reads one topic, recognises it as finished, and clears it in one
 * action. Sorting the same 1,064 rows by due date would give him a list where consecutive rows
 * have nothing to do with each other, and every one would need its own decision.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not close anything by age, and it does not offer a
 * "close everything older than X" button, even though that would empty the list fastest. That is
 * his standing rule, chosen on 2026-08-05 and recorded in `close.ts`: evidence only, never by
 * age. He was offered an age cutoff and refused it, because a genuinely long-running commitment
 * would be swept away with the dead ones and he would stop seeing it. So the machine never
 * decides here; this screen only makes HIS decision cheap to express.
 *
 * The 155 commitments with no topic are grouped under one heading rather than hidden, because a
 * review screen that silently omits a seventh of the backlog is worse than one that admits it.
 */

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import type { DashboardCommitment } from '@rbv/shared';
import { closeCommitments } from './api.js';
import { Icon } from './Icon.js';

/** Matches the server's cap. Named here so the UI can never offer a batch it will refuse. */
const MAX_BATCH = 200;

/** Commitments with no topic, gathered under one honest heading. */
const NO_TOPIC = 'No topic recorded';

export interface Group {
  topic: string;
  items: DashboardCommitment[];
}

/**
 * Group by topic, busiest first.
 *
 * Busiest first because the biggest group is the biggest win per decision, and because a long
 * tail of one-commitment topics at the top would make the screen look like the flat list it is
 * trying not to be.
 *
 * Topics arrive as wikilinks (`[[Vantage launch]]`) from the vault and as bare text from the
 * dashboard payload depending on the path, so the brackets are stripped here rather than
 * assumed absent.
 */
export function groupByTopic(items: DashboardCommitment[]): Group[] {
  const byTopic = new Map<string, DashboardCommitment[]>();
  for (const c of items) {
    const topic = c.topic.replace(/^\[\[|\]\]$/g, '').trim();
    const key = topic === '' ? NO_TOPIC : topic;
    const bucket = byTopic.get(key);
    if (bucket === undefined) byTopic.set(key, [c]);
    else bucket.push(c);
  }
  return [...byTopic.entries()]
    .map(([topic, groupItems]) => ({ topic, items: groupItems }))
    .sort((a, b) => {
      // The untopiced group goes last whatever its size: it is the one group he cannot judge
      // as a unit, so it does not deserve the top of the screen.
      if (a.topic === NO_TOPIC) return 1;
      if (b.topic === NO_TOPIC) return -1;
      if (b.items.length !== a.items.length) return b.items.length - a.items.length;
      return a.topic.localeCompare(b.topic);
    });
}

export function CommitmentReview({
  lane,
  items,
  onClose,
  onChanged,
}: {
  lane: 'owed' | 'waiting';
  /** The WHOLE lane, already fetched. This screen never shows a subset. */
  items: DashboardCommitment[];
  onClose: () => void;
  /** Called after a successful close so every count on the dashboard moves together. */
  onChanged: () => void;
}): JSX.Element {
  const frame = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Closed this session, hidden at once so the list shrinks as he works. */
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  /** Per-path failures from the last batch, kept next to the rows that failed. */
  const [failures, setFailures] = useState<Record<string, string>>({});
  /** Collapsed groups, so a 22-row topic can be folded once judged. */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    frame.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  const live = useMemo(() => items.filter((c) => !gone.has(c.path)), [items, gone]);
  const groups = useMemo(() => groupByTopic(live), [live]);

  const toggle = (path: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const toggleGroup = (g: Group): void =>
    setSelected((s) => {
      const next = new Set(s);
      const allOn = g.items.every((c) => next.has(c.path));
      for (const c of g.items) {
        if (allOn) next.delete(c.path);
        else next.add(c.path);
      }
      return next;
    });

  /**
   * Close what is selected.
   *
   * Batched to the server's cap and NOT optimistic. The single-row Done button elsewhere is
   * optimistic because one row reappearing on failure is easy to notice; forty rows silently
   * returning is not, and this write is the one that touches a lot at once. So it waits, then
   * removes exactly what the server confirmed.
   */
  const closeSelected = async (): Promise<void> => {
    const paths = [...selected].filter((p) => !gone.has(p));
    if (paths.length === 0) return;
    setBusy(true);
    setProblem('');
    setFailures({});

    const confirmedGone = new Set(gone);
    const failedBy: Record<string, string> = {};
    // Chunked, because he can legitimately select more than the cap: 518 in the owed lane.
    for (let i = 0; i < paths.length; i += MAX_BATCH) {
      const batch = paths.slice(i, i + MAX_BATCH);
      const r = await closeCommitments(batch);
      if (!r.ok) {
        setProblem(r.error);
        break;
      }
      for (const p of r.closed) confirmedGone.add(p);
      for (const f of r.failed) failedBy[f.path] = f.error;
    }

    setGone(confirmedGone);
    setFailures(failedBy);
    // Only the confirmed ones leave the selection. A failed row stays selected and ticked, so
    // retrying is one click rather than a hunt through the list.
    setSelected((s) => new Set([...s].filter((p) => !confirmedGone.has(p))));
    setBusy(false);
    onChanged();
  };

  const selectedCount = [...selected].filter((p) => !gone.has(p)).length;
  const closedCount = gone.size;

  return createPortal(
    <div className="mo-frame-backdrop" onClick={onClose}>
      <div
        className="mo-frame mo-review"
        role="dialog"
        aria-modal="true"
        aria-label={lane === 'owed' ? 'Review what you owe' : 'Review what you are waiting on'}
        tabIndex={-1}
        ref={frame}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mo-frame-head">
          <p className="mo-frame-question">
            {lane === 'owed' ? 'What I owe' : 'What I am waiting on'}
            {' · '}
            <span className="mo-review-count">{live.length} open</span>
            {closedCount > 0 && <span className="mo-review-done"> · {closedCount} closed</span>}
          </p>
          <button type="button" className="mo-frame-close" aria-label="Close the review" onClick={onClose}><Icon name="close" size={15} /></button>
        </header>

        {/*
          The rule, stated on the screen rather than only in the code. He will come here wanting
          the list to be shorter, and the fastest way to a short list is the one thing this
          screen refuses to do. Saying so is what stops it reading as a missing feature.
        */}
        <p className="mo-review-rule">
          Nothing here closes by age. Ingestion closes a commitment when later correspondence
          shows it was done; everything else is your call.
        </p>

        {problem !== '' && <p className="mo-social-problem">{problem}</p>}

        <div className="mo-frame-body">
          {groups.length === 0 ? (
            <p className="mo-dash-note">Nothing open in this lane.</p>
          ) : (
            groups.map((g) => {
              const isCollapsed = collapsed.has(g.topic);
              const allOn = g.items.every((c) => selected.has(c.path));
              return (
                <section className="mo-review-group" key={g.topic}>
                  <div className="mo-review-group-head">
                    {/*
                      A group checkbox rather than a "select all" link: it also shows STATE, so
                      a partly-selected group is visible without counting ticks.
                    */}
                    <label className="mo-review-check">
                      <input
                        type="checkbox"
                        checked={allOn}
                        onChange={() => toggleGroup(g)}
                        aria-label={`Select all ${g.items.length} in ${g.topic}`}
                      />
                      <span className="mo-review-topic">{g.topic}</span>
                    </label>
                    <button
                      type="button"
                      className="mo-review-fold"
                      aria-expanded={!isCollapsed}
                      onClick={() =>
                        setCollapsed((s) => {
                          const next = new Set(s);
                          if (next.has(g.topic)) next.delete(g.topic);
                          else next.add(g.topic);
                          return next;
                        })
                      }
                    >
                      {g.items.length} {isCollapsed ? '▸' : '▾'}
                    </button>
                  </div>

                  {!isCollapsed && (
                    <ul className="mo-review-rows">
                      {g.items.map((c) => {
                        const overdue = c.dueInDays !== null && c.dueInDays < 0;
                        const failure = failures[c.path];
                        return (
                          <li className="mo-review-row" key={c.path} data-overdue={overdue}>
                            <label className="mo-review-check">
                              <input
                                type="checkbox"
                                checked={selected.has(c.path)}
                                onChange={() => toggle(c.path)}
                                aria-label={`Select: ${c.text}`}
                              />
                              <span className="mo-review-text">{c.text}</span>
                            </label>
                            <p className="mo-meta">
                              {c.counterparty !== '' && <span className="mo-who">{c.counterparty}</span>}
                              {c.due !== '' && (
                                <span className="mo-due" data-overdue={overdue}>
                                  {c.due}
                                </span>
                              )}
                              {c.blockedOn !== '' && <span>blocked on {c.blockedOn}</span>}
                            </p>
                            {/* A per-row reason, because a batch failure is usually about one note. */}
                            {failure !== undefined && <p className="mo-review-failed">{failure}</p>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })
          )}
        </div>

        {/*
          The action bar is sticky at the bottom, not the top. He selects going down the list,
          so the button belongs where his attention ends up.
        */}
        <footer className="mo-review-actions">
          <button
            type="button"
            className="mo-btn mo-btn-primary"
            disabled={busy || selectedCount === 0}
            onClick={() => void closeSelected()}
          >
            {busy ? 'Closing…' : selectedCount === 0 ? 'Select what is done' : `Mark ${selectedCount} done`}
          </button>
          {selectedCount > 0 && !busy && (
            <button type="button" className="mo-btn" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
